import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Button, TextField } from "@mui/material";
import ValidationError from "@components/ValidationError/ValidationError";
import { FieldValues, useForm } from "react-hook-form";
import { AuthService } from "@service/auth.service";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { setEmail } from "@store/signUpSlice";
import { setMessage } from "@store/snackbarSlice";
import PublicForm from "@components/PublicForm";

const authService = AuthService.instance;

interface VerifyEmailFormType {
  code: string;
}

const VerifyEmailForm: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const email = useSelector((state: RootState) => state.signUp.email);
  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm<VerifyEmailFormType>({
    mode: "onChange",
    criteriaMode: "all",
  });

  const sendCode = async () => {
    if (!email) {
      return;
    }
    try {
      await authService.sendVerificationCode(email);
      dispatch(
        setMessage({
          content: t("verifyEmail.resendCodeSuccessMessage"),
          severity: "success",
        }),
      );
    } catch (e) {
      dispatch(
        setMessage({
          content: t("errors.sendingVerificationCodeError"),
          severity: "error",
        }),
      );
    }
  };

  const verifyEmail = async ({ code }: FieldValues) => {
    if (!code || !email) {
      return;
    }
    try {
      await authService.confirmSignUp(email, code);
      dispatch(setEmail(null));
    } catch (e) {
      const type =
        (e as any).name === "CodeMismatchException"
          ? "verificationCodeMismatch"
          : "unknown";

      dispatch(setMessage({ content: t(`errors.${type}`), severity: "error" }));
    }
  };

  return (
    <PublicForm
      title={t("verifyEmail.title")}
      subtitle={t("verifyEmail.subtitle")}
    >
      <PublicForm.PublicFormContainer
        onSubmit={(e) => {
          clearErrors();
          return handleSubmit(verifyEmail)(e);
        }}
      >
        <TextField
          {...register("code", {
            required: true,
          })}
          error={!!errors?.code}
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          placeholder={t("verifyEmail.codeInput.placeholder")}
          label={t("verifyEmail.codeInput.label")}
        />

        {errors.root && (
          <ValidationError>{t(`errors.${errors.root.type}`)}</ValidationError>
        )}

        <PublicForm.PublicFormActionContainer>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            size="large"
            fullWidth
          >
            {t("verifyEmail.confirmButton")}
          </Button>
        </PublicForm.PublicFormActionContainer>
      </PublicForm.PublicFormContainer>

      <PublicForm.PublicFormFooter>
        <span>{t("verifyEmail.resendLabel")}</span>
        <PublicForm.PublicFormLink to="#" onClick={sendCode}>
          {t("verifyEmail.resendButton")}
        </PublicForm.PublicFormLink>
      </PublicForm.PublicFormFooter>
    </PublicForm>
  );
};

export default VerifyEmailForm;
